import { useQuery } from '@apollo/client'
import { useRouter } from 'next/router'
import * as R from 'ramda'
import { useEffect, useMemo } from 'react'

import { buildPayload } from 'utilities/apollo'
import CurrentUserContext from 'contexts/current-user-context'
import { CoachWorkTimeProvider } from 'providers/coach-work-time-provider'
import { AnalyticsManager } from 'utilities'
import { getCurrentUser } from './queries.graphql'
import bugsnagClient from '../../clients/bugsnag'
import posthog, { isPostHogEnabled } from '../../clients/posthog'
import { useExperimentKit } from 'utilities/experiment/context'

const CurrentUserProvider = ({
  shouldIdentifyUser = true,
  forceNetworkFetch = false,
  children,
}) => {
  const router = useRouter()
  const currentUserResponse = useQuery(getCurrentUser, {
    // Conditionally set fetch policy based on forceNetworkFetch flag
    // If true: use 'network-only' for first fetch, then 'cache-first' (for sign-up flows),
    // This is especially important after sign-up when a new user is created
    // If false: use 'cache-and-network' (default behavior)
    fetchPolicy: forceNetworkFetch ? 'network-only' : 'cache-and-network',
    nextFetchPolicy: forceNetworkFetch ? 'cache-first' : undefined,
  })
  const currentUserPayload = buildPayload(currentUserResponse)

  const isAdmin = R.pathOr(
    false,
    ['data', 'me', 'user', 'coach', 'isAdmin'],
    currentUserPayload
  )

  const contextValue = useMemo(
    () => ({ ...currentUserPayload, isAdmin }),
    [currentUserPayload, isAdmin]
  )

  // Set Bugsnag user data (while converting 'username' to 'name')
  const user = currentUserResponse?.data?.me?.user
  useEffect(() => {
    const keysMap = { username: 'name' }
    bugsnagClient.user = R.unless(
      R.isNil,
      R.pipe(
        R.pick(['id', 'username', 'email']),
        R.toPairs,
        R.transpose,
        R.converge(R.zipObj, [
          R.pipe(
            R.head,
            R.map(key => keysMap[key] || key)
          ),
          R.last,
        ])
      ),
      user
    )
    if (user) {
      // The id only: Kickoff has no BAA with BugSnag.
      bugsnagClient.setUser(user.id)
    }
  }, [user])

  useEffect(() => {
    if (shouldIdentifyUser) {
      AnalyticsManager.identifyUser({
        me: currentUserResponse.data?.me,
      })
    }
  }, [currentUserResponse.data, shouldIdentifyUser])

  // Preserve the existing dashboard PostHog identity for product analytics.
  // Dashboard replay is paused for the Call Guidance /meet pilot; the single
  // recorder owner lives under the meeting's server-side guidance gate.
  const coach = user?.coach
  const userId = user?.id
  const coachId = coach?.id
  const coachLevel = coach?.levelId
  useEffect(() => {
    if (!isPostHogEnabled()) return
    const onDashboard = router.pathname.startsWith('/dashboard')
    if (coachId && onDashboard) {
      posthog.identify(userId, { coachId, coachLevel })
    }
  }, [userId, coachId, coachLevel, router.pathname])

  // useEffect(() => {
  //   if (typeof cookieStore === 'undefined') return

  //   // reload page if signed out from another tab.
  //   // 500ms delay in case sign out occured in this tab.
  //   // only works on Chrome for now
  //   const onCookiesChanged = change => {
  //     if (
  //       change.deleted.length &&
  //       R.any(R.propEq('name', SESSION_TOKEN), change.deleted)
  //     ) {
  //       setTimeout(() => router.reload(), 500)
  //     }
  //   }

  //   window.cookieStore.addEventListener('change', onCookiesChanged)

  //   return () => {
  //     window.cookieStore.removeEventListener('change', onCookiesChanged)
  //   }
  // }, [])

  const ek = useExperimentKit()
  ek.setUser(user)

  return (
    <CurrentUserContext.Provider value={contextValue}>
      {/* Tracks coach work-time on /dashboard/* for every coach (self-gated
          by route + coachId); mounted here so it covers all dashboard pages
          regardless of layout, and sits under the current-user context it
          reads. */}
      <CoachWorkTimeProvider>{children}</CoachWorkTimeProvider>
    </CurrentUserContext.Provider>
  )
}

export default CurrentUserProvider
